import { useEffect, useState } from "react";
import { coverStyle } from "@/lib/cover";
import { useParams } from "react-router-dom";
import axios from "axios";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RsvpAuthGate } from "@/components/RsvpAuthGate";
import { RsvpVerifyNotice } from "@/components/RsvpVerifyNotice";
import { toast } from "sonner";
import { Heart, Check, X, ArrowLeft, Calendar, MapPin, Plus, Trash2 } from "lucide-react";

const DEFAULT_MEALS = ["Chicken", "Beef", "Fish", "Vegetarian", "Vegan"];

export default function PublicNameRSVP() {
  const { slug } = useParams();
  const { user, login, register, refreshUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState("name"); // name | gate | form
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [matched, setMatched] = useState(null); // { token }
  const [guestInfo, setGuestInfo] = useState(null); // no-account contact
  const [pendingSignin, setPendingSignin] = useState(false);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const isPrivate = !!info?.event?.private_page;

  useEffect(() => {
    axios.get(`${API}/public/rsvp-by-name/${slug}`).then(({ data }) => setInfo(data)).catch(() => setNotFound(true));
  }, [slug]);

  const loadForm = async (token) => {
    const { data } = await api.get(`/public/rsvp/${token}`);
    const init = {};
    (data.party || []).forEach((p) => {
      init[p.id] = { guest_id: p.id, status: p.rsvp_status && p.rsvp_status !== "pending" ? p.rsvp_status : "",
        meal_choice: p.meal_choice || "", dietary: p.dietary || "", song_request: p.song_request || "",
        accessibility: p.accessibility || "", plus_ones: toPlusList(p), message: p.message || "" };
    });
    setAnswers(init); setSelected(data); setStage("form");
  };

  const lookup = async (f, l) => {
    const { data } = await axios.post(`${API}/public/rsvp-by-name/${slug}/lookup`, { first_name: f, last_name: l });
    return data.token;
  };

  // Sign-in path: once the account is verified, look up the guest and load the form
  useEffect(() => {
    if (pendingSignin && user && user.email_verified && !selected) {
      lookup(first, last)
        .then((token) => { setMatched({ token }); return loadForm(token); })
        .catch((e) => {
          if (e.response?.status === 404) toast.error("We couldn't find that name on the guest list. Please check the spelling or use \u201cRSVP without an account\u201d.");
          else toast.error("Could not load your RSVP form.");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSignin, user]);

  // Remember the name across a sign-in / email-verification round-trip
  useEffect(() => {
    if (pendingSignin && (!user || !user.email_verified)) {
      localStorage.setItem("rsvpResume", JSON.stringify({ path: window.location.pathname, first, last }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSignin, user]);

  // On return from email verification, auto-resume the sign-in RSVP
  useEffect(() => {
    if (!user || !user.email_verified || pendingSignin) return;
    const raw = localStorage.getItem("rsvpResume");
    if (!raw) return;
    try {
      const r = JSON.parse(raw);
      if (r?.path === window.location.pathname && r.first && r.last) {
        localStorage.removeItem("rsvpResume");
        setFirst(r.first); setLast(r.last); setStage("gate"); setPendingSignin(true);
      }
    } catch (e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const continueName = () => {
    if (!first.trim() || !last.trim()) { toast.error("Please enter both your first and last name."); return; }
    setStage("gate");
  };

  const onNoAccount = async (data) => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/rsvp-by-name/${slug}/resolve`, data);
      setGuestInfo(data); setMatched({ token: res.token });
      await loadForm(res.token);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reset = () => { setStage("name"); setMatched(null); setGuestInfo(null); setPendingSignin(false); setSelected(null); setAnswers({}); setDone(false); setFirst(""); setLast(""); };
  const setAns = (id, patch) => setAnswers((a) => ({ ...a, [id]: { ...a[id], ...patch } }));

  const submit = async () => {
    const list = Object.values(answers);
    if (list.some((a) => !a.status)) { toast.error("Please choose Attending or Declined for each guest."); return; }
    setSaving(true);
    try {
      if (guestInfo) await axios.post(`${API}/public/rsvp/${matched.token}/guest-submit`, { ...guestInfo, answers: list });
      else await api.post(`/rsvp/${matched.token}/submit`, { answers: list });
      setDone(true); toast.success("Your RSVP has been recorded.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (notFound) return <Centered><p className="font-serif text-3xl text-stone-500">Event not found</p></Centered>;
  if (!info) return <Centered><p className="font-serif text-3xl text-stone-300">Loading…</p></Centered>;

  const ev = info.event;

  return (
    <div className="min-h-screen" style={{ background: "#FDFBF7" }} data-testid="name-rsvp-page">
      <div className="relative h-56 sm:h-72 overflow-hidden">
        <img src={ev.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" style={coverStyle(ev)} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative h-full flex flex-col items-center justify-center text-center text-white px-6">
          <Heart size={20} fill="white" className="mb-3" />
          <p className="overline text-white/70">RSVP to</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">{ev.title}</h1>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 mt-4 text-sm text-white/85">
            {ev.date && <span className="flex items-center gap-1.5"><Calendar size={14} /> {formatDate(ev.date)}</span>}
            {ev.venue_name && <span className="flex items-center gap-1.5"><MapPin size={14} /> {ev.venue_name}</span>}
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 py-10">
        {done ? (
          <div className="bg-white border border-[#E8E6E1] rounded-xl p-10 text-center fade-up" data-testid="name-rsvp-success">
            <div className="w-14 h-14 rounded-full bg-[#8C9A86]/15 grid place-items-center mx-auto mb-4"><Check size={26} className="text-[#8C9A86]" /></div>
            <h2 className="font-serif text-3xl">Thank you!</h2>
            <p className="text-stone-500 mt-2 text-sm">{guestInfo ? "Your response has been recorded. Your host will review new guests." : "Your response has been recorded and this event is now saved to your Evermore memories."}</p>
            <div className="mt-6 flex flex-col gap-2">
              <button onClick={reset} data-testid="name-rsvp-another" className="text-sm text-[#8C9A86] hover:underline">RSVP for someone else</button>
              <a href="/app" className="text-sm text-[#8C9A86] hover:underline" data-testid="name-rsvp-memories-link">Go to my Evermore memories →</a>
            </div>
          </div>
        ) : stage === "name" ? (
          <div className="fade-up max-w-md mx-auto">
            <p className="text-center text-sm text-stone-500 mb-5">Enter your name to RSVP.</p>
            <div className="bg-white border border-[#E8E6E1] rounded-xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-medium text-stone-500 mb-1.5 block">First name</span>
                  <input value={first} onChange={(e) => setFirst(e.target.value)} onKeyDown={(e) => e.key === "Enter" && continueName()} placeholder="First name" data-testid="name-rsvp-first"
                    className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-stone-500 mb-1.5 block">Last name</span>
                  <input value={last} onChange={(e) => setLast(e.target.value)} onKeyDown={(e) => e.key === "Enter" && continueName()} placeholder="Last name" data-testid="name-rsvp-last"
                    className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                </label>
              </div>
              <button onClick={continueName} data-testid="name-rsvp-find"
                className="w-full flex items-center justify-center gap-2 rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors">
                Continue
              </button>
            </div>
          </div>
        ) : stage === "gate" && (!user || guestInfo) ? (
          <div className="fade-up max-w-md mx-auto space-y-4">
            <p className="text-center text-sm text-stone-500">Hi <span className="font-medium text-stone-700">{first} {last}</span> — choose how you'd like to RSVP.</p>
            <RsvpAuthGate
              onLogin={async (e, p) => { await login(e, p); setPendingSignin(true); }}
              onRegister={async (n, e, p, r) => { await register(n, e, p, r); setPendingSignin(true); }}
              onNoAccount={onNoAccount}
              allowNoAccount={!isPrivate}
              allowAccount={true}
              defaultFirst={first} defaultLast={last}
              redirectTo={window.location.pathname} />
            {busy && <p className="text-center text-sm text-stone-400" data-testid="name-rsvp-resolving">Loading your RSVP…</p>}
            <div className="text-center">
              <button onClick={reset} data-testid="name-rsvp-different-name" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"><ArrowLeft size={15} /> Use a different name</button>
            </div>
          </div>
        ) : user && !user.email_verified ? (
          <div className="fade-up max-w-md mx-auto space-y-4">
            <RsvpVerifyNotice email={user.email} redirect={window.location.pathname} onRefresh={refreshUser} />
            <div className="text-center">
              <button onClick={reset} data-testid="name-rsvp-verify-back" className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"><ArrowLeft size={15} /> Use a different name</button>
            </div>
          </div>
        ) : !selected ? (
          <p className="text-center text-stone-400 fade-up" data-testid="name-rsvp-loading">Loading your RSVP…</p>
        ) : (
          <div className="space-y-6 fade-up">
            <button onClick={reset} data-testid="name-rsvp-back" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800"><ArrowLeft size={15} /> Start over</button>
            {selected.party.map((g) => (
              <GuestCard key={g.id} guest={g} cfg={selected.rsvp_config} ans={answers[g.id]} setAns={(p) => setAns(g.id, p)} />
            ))}
            <button onClick={submit} disabled={saving} data-testid="name-rsvp-submit"
              className="w-full rounded-full bg-[#2C2A28] text-white py-3 text-sm hover:bg-[#8C9A86] transition-colors disabled:opacity-40">
              {saving ? "Sending…" : "Send response"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GuestCard({ guest, cfg, ans, setAns }) {
  if (!ans) return null;
  const meals = (cfg?.meal_options?.length ? cfg.meal_options : DEFAULT_MEALS);
  const allowance = guest.allowed_plus_ones || 0;
  const plus = ans.plus_ones || [];
  const setPlus = (i, patch) => setAns({ plus_ones: plus.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) });
  const addPlus = () => { if (plus.length < allowance) setAns({ plus_ones: [...plus, { name: "", meal_choice: "", dietary: "" }] }); };
  const removePlus = (i) => setAns({ plus_ones: plus.filter((_, idx) => idx !== i) });
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-xl p-6" data-testid={`name-rsvp-card-${guest.id}`}>
      <p className="overline mb-1">RSVP for</p>
      <h3 className="font-serif text-2xl mb-4">{guest.first_name} {guest.last_name}</h3>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button onClick={() => setAns({ status: "attending" })} data-testid={`name-rsvp-accept-${guest.id}`}
          className={`flex flex-col items-center gap-2 rounded-lg border py-5 text-sm transition-all ${ans.status === "attending" ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-500 hover:border-stone-300"}`}><Check size={20} /> Joyfully accept</button>
        <button onClick={() => setAns({ status: "declined" })} data-testid={`name-rsvp-decline-${guest.id}`}
          className={`flex flex-col items-center gap-2 rounded-lg border py-5 text-sm transition-all ${ans.status === "declined" ? "border-[#C67A7A] bg-[#C67A7A]/10 text-[#9c5757]" : "border-[#E8E6E1] text-stone-500 hover:border-stone-300"}`}><X size={20} /> Regretfully decline</button>
      </div>
      {ans.status === "attending" && (
        <div className="space-y-5 fade-up">
          {cfg?.meal && (
            <div>
              <span className="text-xs font-medium text-stone-500 mb-1.5 block">Meal preference</span>
              <select value={ans.meal_choice} onChange={(e) => setAns({ meal_choice: e.target.value })} data-testid={`name-rsvp-meal-${guest.id}`}
                className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]">
                <option value="">Select a meal…</option>
                {meals.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          {cfg?.dietary && <YesNo label="Any dietary needs or allergies?" value={ans.dietary} onChange={(v) => setAns({ dietary: v })} testid={`name-rsvp-dietary-${guest.id}`} />}
          {cfg?.song && <Field label="Song request" value={ans.song_request} onChange={(v) => setAns({ song_request: v })} testid={`name-rsvp-song-${guest.id}`} />}
          {cfg?.accessibility && <YesNo label="Any accessibility needs?" value={ans.accessibility} onChange={(v) => setAns({ accessibility: v })} testid={`name-rsvp-accessibility-${guest.id}`} />}
          {cfg?.plus_one && allowance > 0 && (
            <div className="space-y-3">
              <span className="text-xs font-medium text-stone-500 block">Guests joining you (up to {allowance})</span>
              {plus.map((po, i) => (
                <div key={i} data-testid={`name-rsvp-plus-card-${guest.id}-${i}`} className="rounded-lg border border-[#E8E6E1] bg-[#FDFBF7] p-3 space-y-2">
                  <div className="flex gap-2">
                    <input placeholder={`Guest ${i + 1} full name`} value={po.name} onChange={(e) => setPlus(i, { name: e.target.value })} data-testid={`name-rsvp-plus-${guest.id}-${i}`}
                      className="flex-1 rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
                    <button onClick={() => removePlus(i)} data-testid={`name-rsvp-plus-remove-${guest.id}-${i}`} className="px-2 text-stone-400 hover:text-[#C67A7A]"><Trash2 size={15} /></button>
                  </div>
                  {cfg?.meal && (
                    <select value={po.meal_choice} onChange={(e) => setPlus(i, { meal_choice: e.target.value })} data-testid={`name-rsvp-plus-meal-${guest.id}-${i}`}
                      className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C9A86]">
                      <option value="">Meal preference…</option>
                      {meals.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                  {cfg?.dietary && (
                    <input placeholder="Dietary needs or allergies (optional)" value={po.dietary} onChange={(e) => setPlus(i, { dietary: e.target.value })} data-testid={`name-rsvp-plus-dietary-${guest.id}-${i}`}
                      className="w-full rounded-md border border-[#E8E6E1] bg-white px-3 py-2 text-sm outline-none focus:border-[#8C9A86]" />
                  )}
                </div>
              ))}
              {plus.length < allowance && <button onClick={addPlus} data-testid={`name-rsvp-add-plus-${guest.id}`} className="flex items-center gap-1.5 text-sm text-[#8C9A86] hover:underline"><Plus size={14} /> Add a guest</button>}
            </div>
          )}
        </div>
      )}
      {ans.status && <div className="mt-5"><Field label="A note to the host (optional)" value={ans.message} onChange={(v) => setAns({ message: v })} testid={`name-rsvp-message-${guest.id}`} /></div>}
    </div>
  );
}

function YesNo({ label, value, onChange, testid }) {
  const [yes, setYes] = useState(!!(value && String(value).trim()));
  const pick = (v) => { setYes(v); if (!v) onChange(""); };
  return (
    <div>
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <div className="flex gap-2">
        <button type="button" onClick={() => pick(true)} data-testid={`${testid}-yes`} className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${yes ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-500 hover:bg-stone-50"}`}>Yes</button>
        <button type="button" onClick={() => pick(false)} data-testid={`${testid}-no`} className={`flex-1 rounded-md border px-3 py-2 text-sm transition-colors ${!yes ? "border-[#8C9A86] bg-[#8C9A86]/10 text-[#5f6b59]" : "border-[#E8E6E1] text-stone-500 hover:bg-stone-50"}`}>No</button>
      </div>
      {yes && <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="Please tell us the details"
        className="mt-2 w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />}
    </div>
  );
}
function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86]" />
    </label>
  );
}
function toPlusList(p) {
  if (Array.isArray(p.plus_ones_detail) && p.plus_ones_detail.length)
    return p.plus_ones_detail.map((d) => ({ name: d.name || "", meal_choice: d.meal_choice || "", dietary: d.dietary || "" }));
  return (p.plus_ones || []).map((n) => (typeof n === "string" ? { name: n, meal_choice: "", dietary: "" } : { name: n?.name || "", meal_choice: n?.meal_choice || "", dietary: n?.dietary || "" }));
}
function Centered({ children }) { return <div className="min-h-screen grid place-items-center" style={{ background: "#FDFBF7" }}>{children}</div>; }
function formatDate(d) { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } }
